/**
 * Lista POSITIVA única das chaves de card de `richContent` — movida de
 * `connectionStore.ts` em 01/10/2026 (STO-6938), byte a byte, para um módulo
 * sem store: `MessageBubble.tsx` deriva daqui o portão de "resposta vazia", e
 * 92 testes da bolha substituem `connectionStore` inteiro por `vi.mock`.
 * `connectionStore` reexporta a lista; nenhum consumidor antigo muda.
 */
import type { RichContent, SuggestedAction } from '../types/rich-content'

/**
 * ÚNICA fonte de verdade das chaves de card que atravessam o fio no turno AO
 * VIVO — o seam engine→chat mais traiçoeiro do repositório.
 *
 * Por que esta lista existe: até 2026-08-03 cada chave era lida por uma `const`
 * própria e repetida em DOIS lugares abaixo (a condição `if` e o objeto
 * literal). Esquecer uma delas não quebrava nada — o motor entregava o card, o
 * chat descartava calado, e o defeito só aparecia na cara do usuário. Já
 * aconteceu três vezes: `equipmentSearchResult` (bug-fiação 2026-07-12),
 * `suggestedActions` (BUG B da mesma data) e `resourceAllocation` (passo 8 do
 * trilho — tipo, componente e branch de render prontos, e a chave morria aqui).
 *
 * Piorava por assimetria: a RELEITURA de conversa antiga
 * (`lib/persisted-presentation.ts:54`) repassa o envelope INTEIRO, então o card
 * podia aparecer depois de um F5 e não aparecer no turno em que o usuário está.
 *
 * Agora a extração é derivada desta lista. Acrescentar um canal é acrescentar
 * UMA linha aqui — e `connectionStore.richContentPassthrough.test.ts` fica
 * vermelho enquanto ela faltar.
 *
 * Fora daqui de propósito: `voiceConfirmation` (não é card — tem estacionamento
 * próprio via `parseVoiceConfirmation`), `toolsUsed` e `processing` (não vêm do
 * envelope verbatim: são remontados) e `onboardingRailHandoff` (o motor permite
 * ignorar, e o texto dele já chega ao usuário dentro de `onboardingRailStep.copy`).
 */
export const RICH_CONTENT_CARD_KEYS = [
  // SDD-001 Phase 5c — envelope do import (proposal/progress/summary/validation).
  'importCard',
  // Recibo REAL do import de plano preventivo (25/08/2026): sucesso E falha. Sem
  // esta linha o motor manda o desfecho e o store o descarta antes de virar
  // pixel — que é literalmente o defeito que este canal nasceu para matar (a
  // tela voltaria a não ter NADA para mostrar quando o import falha).
  'importResult',
  // #426 — snapshot do fluxo de cadastro por card (também alimenta o store vivo).
  'equipmentRegistrationFlow',
  // Fatia 2 da máquina de importação (spec 2026-07-23) — snapshot do ImportFlow
  // (também alimenta o store vivo). Chave fora desta lista é card que o motor
  // manda e o store descarta antes de virar pixel, sem erro em lugar nenhum.
  'importFlow',
  // Layer 2 — confirmação determinística de escrita relida do banco.
  'executionConfirmation',
  // STO-6663/STO-6669 — recibo agregado do lote técnicos+times. Contrato
  // ainda não publicado pelo motor (medido 29/09/2026); chave proposta pela
  // metade da TELA, a confirmar com quem implementar a metade do motor
  // (STO-6663/STO-6664 PARTE 1). Sem esta linha o card, quando o motor
  // mandar, seria descartado antes de virar pixel.
  'teamTechnicianBatchReceipt',
  // Invariante escrita→card — card da entidade persistida, derivado do `_receipt`.
  'entityCard',
  // WS-B4.0 — canal tipado da busca web.
  'webSearchResult',
  // Spec 2026-07-12 (FIX-4) — busca interna no cadastro do próprio tenant.
  'readOnlyListCard',
  'equipmentSearchResult',
  // WS-B4 — cards de dashboard e de planos (números calculados no motor).
  'dashboardCard',
  'plansCard',
  // Cards de leitura da base (spec 2026-07-28, fatias 1/2/3) — consulta a
  // equipes, materiais e notificações.
  'teamsCard',
  'materialsCard',
  'notificationsCard',
  // D5 — cartão de detalhamento da notificação (data, operações, condição). Chave
  // fora desta lista = cartão descartado antes de virar pixel.
  'notificationDetailing',
  // Fase 2 · Camada A — rascunho editável do plano preventivo (também alimenta
  // o store vivo, para a tabela atualizar no lugar em vez de correr com a lista).
  'preventivePlanDraft',
  // Preventivo dia-a-dia (spec v4) — card de entrada, guia e cards plurais.
  'preventivePlanEntryCard',
  'guidanceCard',
  'entityCards',
  // Onda 3 · F4 — cards de DETALHE de plano e de ordem de serviço.
  'planDetail',
  'workOrderDetail',
  'operationsPanel',
  'symptomPrompt',
  // Trilho — passo 5 (plano editável), passo 8 (alocação) e o passo genérico.
  'planEditTable',
  // Funcionalidade 2 (spec 2026-10-16) — comparação do plano do cliente com a
  // referência. Contrato 0.24 já a lista em RICH_CONTENT_ENVELOPE_KEYS.
  'planComparison',
  // procedimento-e-foto — cartão da política de foto (admin). Sem esta linha o
  // motor manda e o store descarta antes de virar pixel.
  'photoPolicyCard',
  'agentPolicy',
  'agentApproval',
  'agentReceipt',
  'agentExplanation',
  'agentPanel',
  'agentRun',
  'agentBreaker',
  'agentTemplates',
  // Card poka-yoke do passo `find_plan` (2026-08-03). Sem esta linha o motor
  // manda o card e o store o DESCARTA antes de virar pixel — a chave existiria
  // no tipo e no render, e a tela seguiria vazia, sem erro em lugar nenhum.
  'equipmentSearchForm',
  // Materiais por tarefa (2026-08-03). MESMA razão da linha acima: chave fora
  // desta lista é card que o motor manda e o store descarta antes de virar
  // pixel, sem erro em lugar nenhum.
  'taskMaterials',
  'resourceAllocation',
  'teamPicker',
  'onboardingRailStep',
  // B4 fatia 4b — pulse NPS 0-10 pós-primeiro-plano. MESMA razão das duas
  // linhas acima: chave fora desta lista é card que o motor manda e o store
  // descarta antes de virar pixel, sem erro em lugar nenhum. Aqui doeria em
  // dobro, porque o motor só emite UMA vez por usuário na vida — um descarte
  // calado não teria segunda chance.
  'npsPulse',
  // STO-76 — sugestão de plano preventivo pós-cadastro (motor:
  // `suggestion-emission.ts:59`, gated por `FF_PREVENTIVE_PLAN_SUGGESTER`). É
  // CARD — tipo espelhado (`types/rich-content.ts`) e branch de render com 3
  // botões (`MessageBubble.tsx`) — e morava em `KNOWN_NON_CARD_RICH_CONTENT_KEYS`
  // (lista para dado sem card: texto, ferramentas, formulário). A classificação
  // errada descartava o card antes de virar pixel — invisível para o portão de
  // paridade (a chave já estava em `KNOWN_RICH_CONTENT_KEYS`, só do lado errado)
  // e para o Sentry (não é chave desconhecida, então `reportUnknownRichContentKeys`
  // nunca dispara).
  'preventivePlanSuggestionCard',
  // STO-186 (execução do STO-171, fecha o STO-66) — a lista genérica
  // (search_work_order/search_technician/search_equipment/search_maint_plan
  // em modo tabela). Era o exemplo vivo do comentário acima: ZERO ocorrências
  // aqui, cinco rodadas seguidas, enquanto o motor já mandava as duas chaves.
  'listCard',
  'listRowDetail',
  // STO-459 — snapshot do OnboardingFlow embutido no turno que armou o import
  // do trilho. Chave fora desta lista é o card "Anexar planilha" (estado
  // `upload`) que o motor manda e o store descarta antes de virar pixel, sem
  // erro em lugar nenhum — era exatamente o defeito da issue.
  'onboardingFlow',
  // STO-1768 — o motor trocou o transporte do cronograma de 52 semanas
  // (commit d9c67b029e, 2026-09-25): `ltp_schedule`/`ltp_equipment_picker`
  // deixaram de viajar JSON-stringificados dentro de `response` (texto) e
  // passaram a vir tipados aqui. Sem esta linha o motor manda o card e o
  // store o descarta em silêncio antes de virar pixel — era literalmente o
  // defeito em produção: `/cronograma` parou de renderizar para o usuário
  // porque o texto do turno nunca mais carrega o JSON que
  // `parseScheduleEnvelope`/`parseEquipmentPickerEnvelope` procuravam.
  'ltpSchedule',
  'ltpEquipmentPicker',
  // Procedimento com foto (spec 26/10/2026, §5) — cartão único `procedureAuthoring`.
  // Chave fora desta lista = cartão que o motor manda e o store descarta calado.
  'procedureAuthoring',
] as const satisfies ReadonlyArray<keyof RichContent>

/**
 * O turno carrega algum card do envelope? Derivado da lista acima — a MESMA que
 * extrai o envelope do fio — para o portão de "resposta vazia" da bolha nunca
 * mais ter lista própria (STO-6938: `resourceAllocation`, `listCard` e
 * `entityCard` faltavam na lista negativa e reenviavam a frase do usuário).
 * Lista vazia não é card (regra de `entityCards` em `pickRichContentCardChannels`).
 */
export function envelopeVesteABolha(rich: Partial<RichContent> | undefined): boolean {
  return RICH_CONTENT_CARD_KEYS.some((key) => {
    const value: unknown = rich?.[key]
    return Array.isArray(value) ? value.length > 0 : value != null
  })
}

/** Preserve the symptom and execution cards on WS; card-only replies are not empty. */
export function workOrderPresentationFromWs(message: Record<string, unknown>): RichContent | undefined {
  const rich = message['richContent'] as Partial<RichContent> | undefined
  const { operationsPanel, workOrderDetail, taskMaterials, resourceAllocation, symptomPrompt } = rich ?? {}
  const materials = taskMaterials?.mode === 'order_execution' ? taskMaterials : undefined
  const allocation = resourceAllocation?.mode === 'order_execution' ? resourceAllocation : undefined
  if (![message['toolsUsed'], message['model'], message['tokens'],
    operationsPanel, workOrderDetail, materials, allocation, symptomPrompt].some(Boolean)) return undefined
  return {
    toolsUsed: Array.isArray(message['toolsUsed'])
      ? (message['toolsUsed'] as string[]).map((name) => ({ name, status: 'success' as const }))
      : undefined,
    ...(operationsPanel ? { operationsPanel } : {}),
    ...(workOrderDetail ? { workOrderDetail } : {}),
    ...(materials ? { taskMaterials: materials } : {}),
    ...(allocation ? { resourceAllocation: allocation } : {}),
    ...(symptomPrompt ? { symptomPrompt } : {}),
  }
}

/** The gateway emits camelCase; retain legacy behavior outside post-team decisions. */
export function workOrderActionsFromWs(message: Record<string, unknown>): SuggestedAction[] | undefined {
  const actions = message['suggestedActions'] as SuggestedAction[] | undefined
  if (Array.isArray(actions) && actions.some((action) => {
    const kind = action?.structuredAction?.correctiveAction?.kind
    return kind === 'set_criticality' || kind === 'duplicate_decision' || kind === 'save_detailing' ||
      Boolean(action?.structuredAction?.orderExecutionAction)
  })) return actions
  return Array.isArray(message['suggested_actions'])
    ? message['suggested_actions'] as SuggestedAction[] : undefined
}
